import { ThemeEvents } from '@theme/events';

const SUB_COLLECTION_ROOT_SELECTOR = '[data-sub-collections-root]';
const SUB_COLLECTION_BUTTON_SELECTOR = '[data-sub-collection-button]';
const SUB_COLLECTION_TOGGLE_SELECTOR = '[data-sub-collections-toggle]';

// Marks the hidden inputs we inject into the facets form to carry the active
// sub-collection values across other filter changes.
const HIDDEN_INPUT_FLAG = 'data-sub-collection-hidden';

const FLOATING_FILTERS_STYLE_ID = 'sub-collections-floating-filters-visibility';

/**
 * Hides the overlay `.floating-filters-toggle` until `.collection-header` has left the viewport (mobile).
 * Styles are injected here so behavior stays with this bundle.
 */
function injectFloatingFiltersVisibilityStyles() {
  if (document.getElementById(FLOATING_FILTERS_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = FLOATING_FILTERS_STYLE_ID;
  style.textContent = `
    @media screen and (max-width: 749px) {
      html[data-floating-filters-bound="true"]:not(.collection-header-offscreen) .floating-filters-toggle {
        visibility: hidden;
        pointer-events: none;
        opacity: 0;
      }

      html[data-floating-filters-bound="true"].collection-header-offscreen .floating-filters-toggle {
        visibility: visible;
        pointer-events: auto;
        opacity: 1;
        transition: opacity 0.2s ease;
      }

      @media (prefers-reduced-motion: reduce) {
        html[data-floating-filters-bound="true"].collection-header-offscreen .floating-filters-toggle {
          transition: none;
        }
      }
    }
  `;
  document.head.appendChild(style);
}

function initFloatingFiltersToggleVisibility() {
  const header = document.querySelector('.collection-header');
  const floating = document.querySelector('.floating-filters-toggle');
  if (!header || !floating) return;

  injectFloatingFiltersVisibilityStyles();
  document.documentElement.setAttribute('data-floating-filters-bound', 'true');

  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (!entry) return;
      document.documentElement.classList.toggle(
        'collection-header-offscreen',
        !entry.isIntersecting
      );
    },
    { threshold: 0, root: null }
  );

  observer.observe(header);
}

/**
 * @param {string} filterName
 * @returns {Set<string>}
 */
function getActiveValuesFromUrl(filterName) {
  if (!filterName) return new Set();

  const params = new URLSearchParams(window.location.search);
  return new Set(params.getAll(filterName));
}

/**
 * @param {string} a
 * @param {string} b
 * @returns {boolean}
 */
function valuesMatch(a, b) {
  if (!a || !b) return false;

  const decode = (value) => {
    try {
      return decodeURIComponent(value.replace(/\+/g, ' '));
    } catch {
      return value.replace(/\+/g, ' ');
    }
  };

  const left = decode(a);
  const right = decode(b);

  return left === right || left.toLowerCase() === right.toLowerCase();
}

/**
 * @param {Set<string>} activeValues
 */
function updateTabStates(activeValues) {
  const tabEls = document.querySelectorAll(SUB_COLLECTION_BUTTON_SELECTOR);

  tabEls.forEach((tabEl) => {
    const role = tabEl.getAttribute('data-sub-collection-role');
    const value = tabEl.getAttribute('data-sub-collection-value') ?? '';
    let isActive = false;

    if (role === 'all') {
      isActive = activeValues.size === 0;
    } else {
      for (const activeValue of activeValues) {
        if (valuesMatch(activeValue, value)) {
          isActive = true;
          break;
        }
      }
    }

    tabEl.classList.toggle('active', isActive);
    tabEl.setAttribute('aria-current', isActive ? 'page' : 'false');
  });
}

function updateBreadcrumbFromActiveTab() {
  const activeValueTab = document.querySelector(
    `${SUB_COLLECTION_BUTTON_SELECTOR}.active[data-sub-collection-role="value"]`
  );
  const activeAllTab = document.querySelector(
    `${SUB_COLLECTION_BUTTON_SELECTOR}.active[data-sub-collection-role="all"]`
  );
  const breadcrumbEl = document.querySelector('[data-selected-sub-collection]');

  if (!breadcrumbEl) return;

  if (activeValueTab) {
    const label =
      activeValueTab.getAttribute('data-sub-collection-label')?.trim() ||
      activeValueTab.textContent?.trim();
    if (label) breadcrumbEl.textContent = label;
    return;
  }

  if (activeAllTab) {
    const label = activeAllTab.textContent?.trim();
    if (label) breadcrumbEl.textContent = label;
  }
}

function handleActiveCategory() {
  const rootEl = document.querySelector(SUB_COLLECTION_ROOT_SELECTOR);
  if (!rootEl) return;

  const filterName = rootEl.getAttribute('data-filter-name');
  const activeValues = getActiveValuesFromUrl(filterName ?? '');

  updateTabStates(activeValues);
  updateBreadcrumbFromActiveTab();
}

/**
 * Builds a URL that toggles a single sub-collection value while preserving every
 * other active value (of this filter and any others). This is what enables
 * multi-selection: clicking an inactive tab adds its value, clicking an active
 * tab removes it.
 *
 * @param {string} filterName
 * @param {string} value
 * @returns {string}
 */
function buildToggledUrl(filterName, value) {
  const url = new URL(window.location.href);
  const { searchParams } = url;

  const existing = searchParams.getAll(filterName);
  const isActive = existing.some((existingValue) => valuesMatch(existingValue, value));

  const remaining = isActive
    ? existing.filter((existingValue) => !valuesMatch(existingValue, value))
    : [...existing, value];

  searchParams.delete(filterName);
  for (const remainingValue of remaining) {
    searchParams.append(filterName, remainingValue);
  }
  searchParams.delete('page');

  return url.toString();
}

/**
 * Intercepts tab clicks so they toggle (rather than replace) the active filter
 * values, then applies the change through the theme's facets form so the grid
 * re-renders without a full page reload. Falls back to normal link navigation
 * when no facets form is present.
 *
 * @param {MouseEvent} event
 */
function handleTabClick(event) {
  if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const tab = target.closest(SUB_COLLECTION_BUTTON_SELECTOR);
  if (!tab) return;

  const root = tab.closest(SUB_COLLECTION_ROOT_SELECTOR);
  const filterName = root?.getAttribute('data-filter-name');
  if (!filterName) return;

  const facetsForm = tab.closest('.shopify-section')?.querySelector('facets-form-component');
  if (!facetsForm || typeof facetsForm.updateFiltersByURL !== 'function') return;

  event.preventDefault();

  const role = tab.getAttribute('data-sub-collection-role');
  let newUrl;

  if (role === 'all') {
    const url = new URL(window.location.href);
    url.searchParams.delete(filterName);
    url.searchParams.delete('page');
    newUrl = url.toString();
  } else {
    const value = tab.getAttribute('data-sub-collection-value') ?? '';
    if (!value) return;
    newUrl = buildToggledUrl(filterName, value);
  }

  facetsForm.updateFiltersByURL(newUrl);
}

/**
 * The sub-collection filter is rendered only as tabs (hidden from the facets
 * pane), so the facets form has no input carrying it. When another filter (size,
 * color, …) is applied, the form rebuilds the URL from its own inputs and would
 * drop the sub-collection selection. To keep them combinable, we mirror the
 * active sub-collection values as hidden inputs inside every facets form.
 */
function syncHiddenFilterInputs() {
  const rootEl = document.querySelector(SUB_COLLECTION_ROOT_SELECTOR);
  if (!rootEl) return;

  const filterName = rootEl.getAttribute('data-filter-name');
  if (!filterName) return;

  const activeValues = [...getActiveValuesFromUrl(filterName)];
  const forms = document.querySelectorAll('facets-form-component form');

  forms.forEach((form) => {
    form.querySelectorAll(`input[${HIDDEN_INPUT_FLAG}]`).forEach((el) => el.remove());

    // If the form already renders this filter (e.g. as checkboxes), it manages
    // the values itself — don't duplicate them.
    if (form.querySelector(`[name="${filterName}"]:not([${HIDDEN_INPUT_FLAG}])`)) return;

    activeValues.forEach((value) => {
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = filterName;
      hidden.value = value;
      hidden.setAttribute(HIDDEN_INPUT_FLAG, '');
      form.appendChild(hidden);
    });
  });
}

/**
 * Keeps the hidden inputs in sync after the section is morphed/re-rendered, so
 * they are always in place before the user interacts with another filter.
 */
function initHiddenInputSync() {
  const section = document.querySelector(SUB_COLLECTION_ROOT_SELECTOR)?.closest('.shopify-section');
  if (!section || section.dataset.subCollectionsSyncBound === 'true') return;

  section.dataset.subCollectionsSyncBound = 'true';
  syncHiddenFilterInputs();

  const observer = new MutationObserver(() => {
    // Disconnect while we mutate so our own inserts don't retrigger the observer.
    observer.disconnect();
    syncHiddenFilterInputs();
    observer.observe(section, { childList: true, subtree: true });
  });

  observer.observe(section, { childList: true, subtree: true });
}

function initShowMoreToggle() {
  const toggle = document.querySelector(SUB_COLLECTION_TOGGLE_SELECTOR);
  const tabs = document.querySelector('.sub-collection-tabs--collapsible');

  if (!toggle || !tabs || toggle.dataset.subCollectionsBound === 'true') return;

  toggle.dataset.subCollectionsBound = 'true';

  toggle.addEventListener('click', () => {
    const isExpanded = toggle.getAttribute('data-expanded') === 'true';
    const nextExpanded = !isExpanded;

    toggle.setAttribute('data-expanded', nextExpanded ? 'true' : 'false');
    tabs.classList.toggle('sub-collection-tabs--expanded', nextExpanded);
  });
}

function initSubCollections() {
  handleActiveCategory();
  initShowMoreToggle();
  initHiddenInputSync();
}

function scheduleSubCollectionSync() {
  requestAnimationFrame(() => {
    requestAnimationFrame(handleActiveCategory);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initSubCollections();
  initFloatingFiltersToggleVisibility();
});

// Delegated so it keeps working after the section is morphed/re-rendered.
document.addEventListener('click', handleTabClick);

document.addEventListener(ThemeEvents.FilterUpdate, scheduleSubCollectionSync);
window.addEventListener('popstate', scheduleSubCollectionSync);
